import { Controller } from "@hotwired/stimulus"

/**
 * Scroll Snap Keyboard Controller
 * - Intercepts arrow keys on SpinShot homepage to scroll one section at a time
 * - Prevents multiple snaps when using down/up arrow keys (browser default scroll + snap can overshoot)
 * - Only active when .spinshot-hero is present and on desktop (matches scroll-snap media query)
 */
export default class extends Controller {
  connect() {
    this.hero = document.querySelector(".spinshot-hero")
    if (!this.hero) return

    this.sections = this.getSnapSections()
    if (this.sections.length === 0) return

    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  getSnapSections() {
    if (window.innerWidth < 768) return []
    const candidates = [
      this.hero,
      ...document.querySelectorAll(".spinshot-hero ~ section"),
      ...document.querySelectorAll(".seo-content-section"),
      document.querySelector(".website-footer")
    ].filter(Boolean)
    return candidates.sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    )
  }

  handleKeydown(e) {
    if (window.innerWidth < 768) return
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return
    if (e.target.closest("input, textarea, select, [contenteditable]")) return

    e.preventDefault()

    const scrollY = window.scrollY
    const viewportCenter = scrollY + window.innerHeight * 0.5

    let currentIndex = -1
    for (let i = 0; i < this.sections.length; i++) {
      const rect = this.sections[i].getBoundingClientRect()
      const top = rect.top + scrollY
      const bottom = top + rect.height
      if (viewportCenter >= top && viewportCenter <= bottom) {
        currentIndex = i
        break
      }
    }
    if (currentIndex === -1) {
      currentIndex = this.sections.findIndex((s) => {
        const rect = s.getBoundingClientRect()
        return rect.top + window.scrollY > scrollY
      })
      if (currentIndex === -1) currentIndex = 0
    }

    let targetIndex
    if (e.key === "ArrowDown") {
      targetIndex = Math.min(currentIndex + 1, this.sections.length - 1)
    } else {
      targetIndex = Math.max(currentIndex - 1, 0)
    }

    if (targetIndex === currentIndex) return

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.sections[targetIndex].scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start"
    })
  }
}
